import {
  Commitment,
  nextCycleDate,
} from "@/services/backend/relationships/types/relationship";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import t from "@/lang/lang";
import { CalendarSync } from "lucide-react";
import { useEffect, useState } from "react";
import { useRelationshipsRelationshipCreateSeriesMutation } from "@/services/backend/relationships/api/service";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { DateInput } from "@/shared/components/form/date-input";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { NullUUID } from "@bitory-gmbh/nidavellir";

export function CreateSeriesDialog({
  commitment,
  onSuccess,
  disabled,
  hidden,
}: {
  commitment?: Commitment;
  onSuccess?: () => void;
  disabled?: boolean;
  hidden?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState({
    id: commitment ? commitment.relationshipId : NullUUID,
    commitmentId: commitment ? commitment.id : NullUUID,
    firstDate: commitment ? nextCycleDate(commitment) : NullUUID,
  });
  const [post, { isLoading, error, isSuccess, reset }] =
    useRelationshipsRelationshipCreateSeriesMutation();

  useEffect(() => {
    if (isSuccess) {
      reset();
      setOpen(false);
      if (onSuccess) onSuccess();
    }
  }, [isSuccess, onSuccess, reset]);

  const doCreate = () => {
    if (isLoading) {
      return;
    }

    post(request);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger
        icon={CalendarSync}
        label={t("Serie erstellen")}
        disabled={disabled || !commitment}
        size="sm"
        hidden={hidden}
      />
      <DialogContent>
        <CommonDialogHeader
          title={t("Serie erstellen")}
          description={t(
            "Erstellen Sie eine Serie von Sollstellungen basierend auf der gewählten Sollstellung. Es kann nur eine Serie erstellt werden, wenn die Sollstellung noch nicht Teil einer Serie ist.",
          )}
        />
        <DateInput
          label={t("Erstes Datum")}
          date={request.firstDate}
          onDateChange={(firstDate) =>
            setRequest({ ...request, firstDate: firstDate ?? "" })
          }
          errors={inputValidationErrorMessages(error, "firstDate")}
          hint={t(
            "Standardmäßig ist das erste Datum ein Turnus nach dem Datum dieser Sollstellung. Wenn ein anders Datum passender ist, kann das hier angegeben werden. Das Datum dieser Sollstellung wird nicht geändert.",
          )}
        />
        <RTKQueryErrorAlert error={error} onDismiss={() => reset()} />
        <CommonDialogFooter isLoading={isLoading} onClickAction={doCreate} />
      </DialogContent>
    </Dialog>
  );
}
